import React, { useEffect, useRef, useState } from "react";
import "./Dashboard.css";
import DynamicTable, { DynamicTableRef } from "./DynamicTable";
import { useAppContext } from "../../context/appcontext";
import {
  fetchDashboardPending,
  fetchDashboardDraft,
  fetchDashboardResubmitted,
  fetchDashboardRequest,
  fetchDashboardReview,
  fetchDashboardActionedRequest,
  fetchDashboardAllRequest,
  fetchTotalCountDashboard,
} from "../../redux/slices/dashboardSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { disableScroll, enableScroll } from "../../services/commonService";
import { useLocation, useNavigate } from "react-router-dom";

interface Application {
  id: string;
  name: string;
}

const Dashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const navigate = useNavigate();
  const { loggedinuser, authChecked } = useAppContext();

  const { data, totalCounts } = useSelector((state: RootState) => state.dashboard);

  const userId = loggedinuser.id;
  const role = loggedinuser.roles || "";

  // ---------------------------------------------
  // COMPANY & FILTERS
  // ---------------------------------------------
  const [company, setCompany] = useState<string>("");
const [companyId, setCompanyId] = useState<string | null>(null);
  const [dataCompanies, setDataCompanies] = useState<Application[]>([]);


  // ---------------------------------------------
  // SORTING
  // ---------------------------------------------

  const [displaySortColumn, setDisplaySortColumn] = useState<string | null>(null);


  // ---------------------------------------------
  // COUNTERS LOADING
  // ---------------------------------------------
  const [isLoadingCounter, setIsLoadingCounter] = useState(true);

  // ---------------------------------------------
  // TABS
  // ---------------------------------------------
  const getTabKey = (tab: string | null | undefined) => {
    switch (tab) {
      case "My Drafts":
        return "drafts";
      case "My Requests":
        return "myrequest";
      case "My Reviews":
        return "reviews";
      case "My Actioned Requests":
        return "actioned";
      case "All Requests":
        return "allrequests";
      case "My Pending Resubmissions":
        return "resubmission";
      default:
        return "pending-approvals";
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const tabFromUrl = queryParams.get("tab");



  const restoredPage =
  (location.state?.page ?? Number(queryParams.get("page"))) || 1;

const restoredSize =
  (location.state?.size ?? Number(queryParams.get("size"))) || 10;

const restoredFilters = location.state?.filters ?? null;
const restoredSortCol = location.state?.sortColumn ?? null;
const restoredSortOrd = location.state?.sortOrder ?? "desc";
const restoredTab =
  location.state?.tab ?? queryParams.get("tab") ?? "My Pending Approvals";



 const [activeTab, setActiveTab] = useState(getTabKey(restoredTab));
const [currentPage, setCurrentPage] = useState(restoredPage);
const [recordsPerPage, setRecordsPerPage] = useState(restoredSize);
const [filters, setFilters] = useState(
  restoredFilters ?? { userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone }
);
const [sortColumn, setSortColumn] = useState(restoredSortCol);
const [sortOrder, setSortOrder] = useState(restoredSortOrd);
const [totalPages, setTotalPages] = useState(1);
  // ---------------------------------------------
  // UI & LOAD STATE
  // ---------------------------------------------
  const [loading, setLoading] = useState(false);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [redirect_url, setRedirectUrl] = useState("");

  // ---------------------------------------------
  // URL RESTORE FLAGS
  // ---------------------------------------------
  const urlCleaned = useRef(false);
  const restoringRef = useRef(true);

  // ---------------------------------------------
  // COUNT FETCH (ONCE)
  // ---------------------------------------------
  useEffect(() => {
    if (!userId) return;

    setIsLoadingCounter(true);
    disableScroll();

 let resolveCompanyId: string | null = null;

  if (!role.toLowerCase().includes("super")) {
    resolveCompanyId = loggedinuser.companyId; // user ki company
    setCompanyId(loggedinuser.companyId);
  } else {
    // SUPERADMIN → companyId null
    setCompanyId(null);
  }

    dispatch(fetchTotalCountDashboard({ userId, companyId: resolveCompanyId }))
      .finally(() => {
        setIsLoadingCounter(false);
        enableScroll();
      });
  }, [userId]);

    // ---------------------------------------------------
  // RESTORE URL CLEANING (ONCE)
  // ---------------------------------------------------
  useEffect(() => {
    if (urlCleaned.current) return; // already cleaned
    if (restoringRef.current) return; // restore not finished

    if (location.search) {
      urlCleaned.current = true;
      navigate("/dashboard", { replace: true });
    }
  }, [restoringRef.current]);

  // Mark restoring complete
  useEffect(() => {
    restoringRef.current = false;
  }, []);

  // ---------------------------------------------------
  // LOAD DATA (fetchData signature preserved exactly)
  // ---------------------------------------------------
  const fetchData = async (
    passedFilters: {} = filters,
    page: number,
    numberOfRecord: number,
    userIdParam: string,
    sortCol?: string,
    sortOrd: "asc" | "desc" = "desc",
    tab?: string,
    companyFilter?: string
  ) => {
    setLoading(true);
    setInitialDataLoaded(false);

    try {
      let response;

      const args = {
        filters: passedFilters,
        page,
        numberOfRecord,
        userId: userIdParam,
        sortColumn: sortCol,
        sortOrder: sortOrd,
      };

      switch (tab) {
        case "resubmission":
          response = await dispatch(fetchDashboardResubmitted(args)).unwrap();
          setRedirectUrl("pendingRequests");
          break;

        case "drafts":
          response = await dispatch(fetchDashboardDraft(args)).unwrap();
          setRedirectUrl("pendingRequests");
          break;

        case "myrequest":
          response = await dispatch(fetchDashboardRequest(args)).unwrap();
          setRedirectUrl("readRequests");
          break;

        case "reviews":
          response = await dispatch(fetchDashboardReview(args)).unwrap();
          setRedirectUrl("readRequests");
          break;

        case "actioned":
          response = await dispatch(fetchDashboardActionedRequest(args)).unwrap();
          setRedirectUrl("readRequests");
          break;

        case "allrequests":
          response = await dispatch(
            fetchDashboardAllRequest({
              ...args,
              companyId,
            })
          ).unwrap();
          setRedirectUrl("readRequests");
          break;

        default:
          response = await dispatch(fetchDashboardPending(args)).unwrap();
          setRedirectUrl("readRequests");
          break;
      }

      if (response?.totalCount !== undefined) {
        const pages =
          response.totalCount === 0
            ? 1
            : Math.ceil(response.totalCount / numberOfRecord);
        setTotalPages(pages);
      }
    } catch (err) {
      console.error("Dashboard fetch error:", err);
    } finally {
      setLoading(false);
      setInitialDataLoaded(true); // tabs unlock when complete
    }
  };

  // ---------------------------------------------------
  // RESTORE PAGE & SIZE FIRST (THEN real fetch)
  // ---------------------------------------------------
 useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const savedPage = Number(params.get("page"));
  const savedSize = Number(params.get("size"));

  if (savedPage) setCurrentPage(savedPage);
  if (savedSize) setRecordsPerPage(savedSize);

  // WAIT 1 render so state updates apply FIRST
  setTimeout(() => {
    setInitialFetchDone(true);
  }, 0);
}, []);

  // ---------------------------------------------------
  // MAIN FETCH EFFECT (no double-fetch, no flicker)
  // ---------------------------------------------------
  useEffect(() => {
    if (!initialFetchDone) return; // avoid early calls

    fetchData(
      filters,
      currentPage,
      recordsPerPage,
      userId,
      sortColumn || undefined,
      sortOrder,
      activeTab
    );
  }, [initialFetchDone, currentPage, recordsPerPage, activeTab]);

  // ---------------------------------------------------
  // SORTING HANDLER
  // ---------------------------------------------------
  const columnMapping = {
    RequestId: "RequestID",
    Application: "applicationTitle",
    Subtype: "subtypeTitle",
    Status: "formActionString",
    SubmittedDate: "submittedDateTime",
    Subject: "subject",
    Requester: "requestorName",
    companyName: "companyName",
    CompanyCode: "companyName",
  };

  const handleSort = ({ column, order }) => {
    const backendColumn = columnMapping[column];

    if (!backendColumn) return;

    setSortColumn(backendColumn);
    setDisplaySortColumn(column);
    setSortOrder(order);

    fetchData(
      filters,
      currentPage,
      recordsPerPage,
      userId,
      backendColumn,
      order,
      activeTab
    );
  };

  // ---------------------------------------------------
  // TAB CLICK HANDLER (tabs disabled until load complete)
  // ---------------------------------------------------
  const tableRef = useRef<DynamicTableRef>(null);
const handleTabClick = (tabId) => {
  if (loading || !initialDataLoaded) return;
  tableRef.current?.resetTab(tabId);
  const defaultFilters = {
    search: null,
    userTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
  
  setFilters(defaultFilters);
  setDisplaySortColumn(null);
  setSortColumn(null);
  setSortOrder("desc");

  setSearchQuery("");

  // FIXED TYPECAST
  const searchInput = document.getElementById("search-input") as HTMLInputElement | null;
  if (searchInput) searchInput.value = "";

  setActiveTab(tabId);
  setCurrentPage(1);
    setRecordsPerPage(10);
};

  // Pagination handler
  const handlePageChange = (p) => setCurrentPage(p);

  // Page size handler
  const handleEntriesChange = (e) => {
    const val = Number(e.target.value);
    setRecordsPerPage(val);
    setCurrentPage(1);
  };

  // Search
  const [searchQuery, setSearchQuery] = useState("");
  const handleSearchChange = (e) => setSearchQuery(e.target.value);

    // ---------------------------------------------------
  // DATE HEADER
  // ---------------------------------------------------
  const date = new Date();
  const currentDay = date.toLocaleDateString("en-GB", { weekday: "long" });
  const formattedDate = date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });

  // Search reset on tab change
  useEffect(() => {
    setSearchQuery("");
  }, [activeTab]);

  // Filter search (local)
  let filteredData =
    data?.filter((item: any) =>
      item?.Application?.toLowerCase().includes(searchQuery.toLowerCase())
    ) ?? [];

  // ---------------------------------------------------
  // FINAL RETURN UI
  // ---------------------------------------------------
  return (
    authChecked && (
      <div className="p-6">

        {/* HEADER */}
        <div className="dashboard-header flex smallerScreen">
          <h5
            className="text-3xl font-bold"
            style={{
              fontWeight: 600,
              fontFamily: "'Open Sans', sans-serif",
              fontSize: "20px",
              margin: "0 0 8px",
            }}
          >
            Dashboard
          </h5>

          <div
            className="text-3xl font-bold"
            style={{
              fontWeight: 700,
              fontFamily: "'Open Sans', sans-serif",
              fontSize: "16px",
              margin: "0 0 5px",
              textAlign: "right",
            }}
          >
            {`Today is ${currentDay}, ${formattedDate}`}
          </div>
        </div>

        <hr style={{ margin: 0, padding: 0 }} />

        {/* ---------------------------------------------------
            TABS — disabled until initialDataLoaded = true
        --------------------------------------------------- */}
        <div className="dbtabs">
          <div className="master-data">
            <div
              className={
                role.toLowerCase().includes("admin")
                  ? "tabs-row tabs-06"
                  : ""
              }
            >
              <ul
                className="nav nav-tabs row row-gettor"
                id="myTab"
                role="tablist"
                style={{ margin: 0 }}
              >
                {/* REUSABLE TAB GENERATOR */}
                {[
                  {
                    key: "pending-approvals",
                    label: "My Pending Approvals",
                    count: totalCounts.myPendingApprovals,
                  },
                  {
                    key: "resubmission",
                    label: "My Pending Resubmissions",
                    count: totalCounts.myPendingResubmissions,
                  },
                  {
                    key: "drafts",
                    label: "My Drafts",
                    count: totalCounts.myDrafts,
                  },
                  {
                    key: "myrequest",
                    label: "My Requests",
                    count: totalCounts.myRequests,
                  },
                  {
                    key: "reviews",
                    label: "My Reviews",
                    count: totalCounts.myReviews,
                  },
                  {
                    key: "actioned",
                    label: "My Actioned Requests",
                    count: totalCounts.myActionedRequests,
                  },
                ]
                  .concat(
                    role.toLowerCase().includes("admin")
                      ? [
                          {
                            key: "allrequests",
                            label: "All Requests",
                            count: totalCounts.allRequests,
                          },
                        ]
                      : []
                  )
                  .map((tabItem) => (
                    <li
                      key={tabItem.key}
                      className={`nav-item col-lg-2 col-gettor ${
                        activeTab === tabItem.key ? "active" : ""
                      }`}
                      style={{
                        paddingLeft: "1px",
                        paddingRight: "1px",
                        cursor: initialDataLoaded ? "pointer" : "no-drop",
                      }}
                    >
                      <a
                        className={`nav-link ${
                          activeTab === tabItem.key ? "active" : ""
                        } ${!initialDataLoaded ? "disabled-link" : ""}`}
                        onClick={() => {
                          if (!initialDataLoaded) return;
                          if (activeTab === tabItem.key) return;
                          handleTabClick(tabItem.key);
                        }}
                      >
                        <span>
                          {isLoadingCounter ? (
                            <div
                              className="spinner-border cpunterLoader"
                              role="status"
                            >
                              <span className="sr-only">Loading...</span>
                            </div>
                          ) : (
                            tabItem.count
                          )}
                        </span>
                        <h4>{tabItem.label}</h4>
                      </a>
                    </li>
                  ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ---------------------------------------------------
             TABLE
        --------------------------------------------------- */}
        <DynamicTable
          ref={tableRef}
          tabState={activeTab}
          data={filteredData}
          totalPage={totalPages}
          recordsPerPage={recordsPerPage}
          handleChange={handleEntriesChange}
          handlePageChange={handlePageChange}
          loading={loading}
          searchQuery={searchQuery}
          page={currentPage.toString()}
          fetchData={fetchData}
          redirect_url={redirect_url}
          setFilters={setFilters}
          filters={filters}
          sortColumn={displaySortColumn}
          sortOrder={sortOrder}
          onSort={handleSort}
          companyId={companyId}
          setCompanyId={setCompanyId}
          keySort={sortColumn}
          company={company}
          setCompany={setCompany}
          dataCompanies={dataCompanies}
          id={`tab-${activeTab}`}
           disableInitialFetch={true}
        />
      </div>
    )
  );
};

export default Dashboard;
